/*
  Offer LP — page behaviour.

  Seven independent custom elements:

    <offer-lp-sticky>    the mobile price bar
    <offer-lp-showcase>  the per-feature video clips
    <offer-lp-colors>    the color panes
    <offer-lp-fit>       the fit-video filter and lightbox
    <offer-lp-gallery>   the hero photo carousel
    <offer-lp-photos>    the review-photo lightbox
    <offer-lp-clips>     the detail clips, attached on approach

  Custom elements rather than a DOMContentLoaded pass, so each one upgrades
  correctly when the theme editor re-renders a single section.
*/

(() => {
  'use strict';

  const DESKTOP_QUERY = '(min-width: 741px)';

  /* ------------------------------------------------------- sticky price bar */

  class OfferLpSticky extends HTMLElement {
    connectedCallback() {
      this.scrolledPast = false;
      this.targetVisible = false;

      this.target = document.querySelector(this.dataset.watch || '#build-your-bundle');
      this.showAfter = parseInt(this.dataset.showAfter, 10);
      if (Number.isNaN(this.showAfter)) this.showAfter = 600;

      // Hide while the buy box is on screen so the bar never covers the thing
      // it points at.
      if (this.target && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.targetVisible = entry.isIntersecting;
            this.update();
          },
          { threshold: 0 }
        );
        this.observer.observe(this.target);
      }

      this._onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      this.observer?.disconnect();
    }

    onScroll() {
      this.scrolledPast = window.pageYOffset > this.showAfter;
      this.update();
    }

    update() {
      this.classList.toggle('is-visible', this.scrolledPast && !this.targetVisible);
    }
  }

  /* ------------------------------------------------------ feature showcase */

  /**
   * A clip per feature claim.
   *
   * Desktop: the clips are stacked in one column and only the highlighted
   * claim's clip is showing. It advances on its own so all four get seen — a
   * visitor who never hovers anything would otherwise only ever watch the
   * first one — and stops advancing for good the moment she picks a claim
   * herself, because a panel that keeps moving after you have taken control
   * reads as broken.
   *
   * Mobile: every clip is visible above its own claim, and only the one on
   * screen plays.
   *
   * Nothing here is required to understand the section. Every claim is present
   * as text at every breakpoint, the clips are aria-hidden illustrations of it,
   * and with the script blocked the layout is the same list plus posters.
   */
  const FALLBACK_CLIP_MS = 5000;

  class OfferLpShowcase extends HTMLElement {
    connectedCallback() {
      this.clips = Array.from(this.querySelectorAll('[data-clip]'));
      this.rows = Array.from(this.querySelectorAll('[data-row]'));
      if (this.clips.length === 0) return;

      this.index = 0;
      this.onScreen = false;
      this.desktop = window.matchMedia(DESKTOP_QUERY);

      // Reduced motion: no autoplay and no advancing. The poster stands in,
      // and the clips get native controls so they are still watchable.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.manual = true;
        this.classList.add('is-manual');
        this.videos().forEach((video) => {
          video.controls = true;
        });
      } else {
        this.manual = false;
      }

      this._onRowActivate = this.onRowActivate.bind(this);
      this.rows.forEach((row) => {
        row.addEventListener('click', this._onRowActivate);
        row.addEventListener('mouseenter', this._onRowActivate);
      });

      this._onBreakpoint = () => this.setup();
      this.desktop.addEventListener('change', this._onBreakpoint);

      this.setup();
    }

    disconnectedCallback() {
      this.stopAdvancing();
      this.disconnectObservers();
      this.desktop.removeEventListener('change', this._onBreakpoint);
      this.rows.forEach((row) => {
        row.removeEventListener('click', this._onRowActivate);
        row.removeEventListener('mouseenter', this._onRowActivate);
      });
    }

    videos() {
      return this.clips.map((clip) => clip.querySelector('video')).filter(Boolean);
    }

    disconnectObservers() {
      this.sectionObserver?.disconnect();
      this.clipObserver?.disconnect();
      this.sectionObserver = null;
      this.clipObserver = null;
    }

    setup() {
      this.stopAdvancing();
      this.disconnectObservers();

      if (this.desktop.matches) {
        this.setupDesktop();
      } else {
        this.setupMobile();
      }
    }

    setupDesktop() {
      this.clips.forEach((clip, i) => clip.classList.toggle('is-active', i === this.index));
      this.rows.forEach((row, i) => row.classList.toggle('is-active', i === this.index));

      // Only advance while the section is actually on screen, so she doesn't
      // scroll back to a panel that has cycled three claims past where she
      // left it.
      if ('IntersectionObserver' in window) {
        this.sectionObserver = new IntersectionObserver(
          ([entry]) => {
            this.onScreen = entry.isIntersecting;
            if (this.onScreen) {
              this.activate(this.index);
            } else {
              this.stopAdvancing();
              this.pauseAll();
            }
          },
          { threshold: 0.25 }
        );
        this.sectionObserver.observe(this);
      } else {
        this.onScreen = true;
        this.activate(this.index);
      }
    }

    setupMobile() {
      // Every clip shows on a phone, so the desktop highlight would just dim
      // three quarters of the copy for no reason.
      this.clips.forEach((clip) => clip.classList.remove('is-active'));
      this.rows.forEach((row) => row.classList.remove('is-active'));

      if (!('IntersectionObserver' in window)) {
        this.clips.forEach((clip) => this.load(clip));
        return;
      }

      this.clipObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target.querySelector('video');
            if (!video) return;

            if (entry.isIntersecting) {
              this.load(entry.target);
              if (!this.manual) this.play(video);
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.5 }
      );

      this.clips.forEach((clip) => this.clipObserver.observe(clip));
    }

    onRowActivate(event) {
      if (!this.desktop.matches) return;

      this.manual = true;
      this.classList.add('is-manual');
      this.stopAdvancing();

      const index = Number(event.currentTarget.dataset.row);
      if (Number.isInteger(index)) this.activate(index);
    }

    activate(index) {
      this.index = index;

      this.clips.forEach((clip, i) => clip.classList.toggle('is-active', i === index));
      this.rows.forEach((row, i) => row.classList.toggle('is-active', i === index));

      const clip = this.clips[index];
      this.pauseAll(clip);
      this.load(clip);

      const video = clip?.querySelector('video');
      if (video && !this.manual) this.play(video);

      if (!this.manual && this.onScreen) this.scheduleAdvance(video);
    }

    /**
     * Attaches the source on first use. The clips ship with data-src and
     * preload="none" so a section with four of them downloads the one on
     * screen instead of all four on page load.
     */
    load(clip) {
      const video = clip?.querySelector('video[data-src]');
      if (!video) return;

      video.src = video.dataset.src;
      delete video.dataset.src;
    }

    play(video) {
      // Rejects on low-power mode and on any browser that declines to autoplay.
      // The poster is already showing, so there is nothing to recover from.
      video.play?.().catch(() => {});
    }

    pauseAll(except) {
      this.clips.forEach((clip) => {
        if (clip === except) return;
        clip.querySelector('video')?.pause();
      });
    }

    /**
     * Advances after one pass of the clip. The duration is not known until the
     * metadata lands, so this schedules against a five-second default first and
     * corrects itself once the real length arrives.
     */
    scheduleAdvance(video) {
      this.stopAdvancing();

      const known = video && Number.isFinite(video.duration) && video.duration > 0;
      const ms = known ? Math.round(video.duration * 1000) : FALLBACK_CLIP_MS;

      // Keeps the progress rail in step with the clip it is timing.
      this.rows[this.index]?.style.setProperty('--olp-clip-duration', `${ms}ms`);

      if (video && !known) {
        video.addEventListener('loadedmetadata', () => this.scheduleAdvance(video), {
          once: true
        });
      }

      this.advanceTimer = window.setTimeout(() => {
        this.activate((this.index + 1) % this.clips.length);
      }, ms);
    }

    stopAdvancing() {
      window.clearTimeout(this.advanceTimer);
    }
  }

  /* ------------------------------------------------------- detail clips */

  /**
   * <offer-lp-clips> — ambient clips that attach their own source.
   *
   * Wraps a list whose rows may each carry a short looping clip. The clips ship
   * from offer-lp-media in `managed` mode, meaning data-src and
   * preload="none" and no autoplay, so nothing is requested until this element
   * decides it is worth requesting.
   *
   * That decision is the whole point. These eight clips previously shipped with
   * a real src and `autoplay`, and autoplay defeats preload: a muted looping
   * video plays the moment it can, which means pulling the file down. Measured
   * over the first ten seconds of a desktop load, the page fetched 86.7MB of
   * video, 66MB of it these eight clips — for clips that sit thousands of pixels
   * down, behind a heading, demonstrating a detail she may never reach. With
   * them deferred the same load fetches 3.8MB. The audience here is women in
   * their sixties arriving from an ad, a good share of them on a phone on
   * cellular.
   *
   * Measure this with CDP `Network.dataReceived`, not by summing content-length:
   * a 206 response advertises the whole remaining file, so adding those up
   * reports tens of megabytes the browser aborted before taking.
   *
   * Distinct from <offer-lp-showcase>, which drives one clip at a time
   * through a stack and owns which is playing. This owns nothing but whether a
   * source exists yet: every row's clip plays independently, because the rows
   * are a list she reads rather than a sequence she is walked through.
   */
  class OfferLpClips extends HTMLElement {
    connectedCallback() {
      this.clips = Array.from(this.querySelectorAll('video[data-src]'));
      if (this.clips.length === 0) return;

      // Reduced motion: attach the source so the first frame stands in for the
      // clip, but never play it, and hand over native controls so it is still
      // watchable. Same bargain <offer-lp-showcase> makes.
      this.manual = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Without IntersectionObserver there is no signal to attach on, and a
      // detail row showing a permanently empty box is worse than an eager
      // download. Attach everything and let the browser decide.
      if (!window.IntersectionObserver) {
        this.clips.forEach((clip) => this.attach(clip));
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => entries.forEach((entry) => this.onIntersect(entry)),
        {
          // A screen of lead time, so the clip has started arriving by the time
          // it is looked at. Attaching exactly at the viewport edge trades an
          // eager download for a visible wait, which is not the trade intended.
          rootMargin: '400px 0px',
          threshold: 0
        }
      );

      this.clips.forEach((clip) => this.observer.observe(clip));
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    onIntersect(entry) {
      const clip = entry.target;

      if (!entry.isIntersecting) {
        // Paused rather than torn down. The bytes are already spent, so
        // dropping the source would only mean paying for them twice on the way
        // back up; what pausing saves is the decode, which runs for as long as
        // a looping clip is playing whether or not anyone is watching.
        if (!clip.paused) clip.pause();
        return;
      }

      this.attach(clip);
      if (!this.manual) this.play(clip);
    }

    attach(clip) {
      const src = clip.dataset.src;
      if (!src) return;

      // Removed first, so this is a one-way door: the observer fires again on
      // every crossing and re-setting src would restart the download.
      delete clip.dataset.src;

      // Metadata, not none, now that a source exists. The element still has to
      // paint a first frame, and preload="none" would leave it blank until
      // play() — which never comes under reduced motion.
      clip.preload = 'metadata';
      clip.src = src;

      if (this.manual) clip.controls = true;
    }

    /**
     * Autoplay can still be refused — a battery-saver mode will reject it even
     * for a muted clip — and the rejection arrives as a promise. Swallowed
     * rather than handled: the frame is already on screen, so the failure state
     * is a still image of the thing the row is describing, which is the same
     * thing the poster would have given us.
     */
    play(clip) {
      clip.play()?.catch(() => {});
    }
  }

  /* --------------------------------------------------------- color panes */

  /**
   * Two stages, one per garment, each changing color on its own.
   *
   * They are not synced. Four of the colors are in both pieces, one is
   * jacket-only and three are vest-only, so syncing would have to either blank
   * a pane out or pretend a color exists that doesn't. Someone who wants a
   * navy jacket and a maroon vest is not making a mistake.
   *
   * Two levels of selection: the color, and then which photograph of that
   * color. Changing color always resets to that color's first view, because
   * holding the view index across a color change means clicking Maroon can
   * land on a blank stage when Maroon has fewer photographs than Navy did.
   *
   * With the script blocked each pane shows its opening color and the
   * thumbnails still name every color on offer, which is more than the page
   * showed before this section existed.
   */
  class OfferLpColors extends HTMLElement {
    connectedCallback() {
      this.panes = Array.from(this.querySelectorAll('[data-colors-pane]')).map((root) => ({
        root,
        title: root.dataset.title || '',
        shots: Array.from(root.querySelectorAll('[data-colors-shot]')),
        swatches: Array.from(root.querySelectorAll('[data-colors-swatch]')),
        viewRows: Array.from(root.querySelectorAll('[data-colors-views]')),
        views: Array.from(root.querySelectorAll('[data-colors-view]')),
        current: root.querySelector('[data-colors-current]'),
        color:
          root.querySelector('[data-colors-swatch][aria-pressed="true"]')?.dataset.color ||
          root.querySelector('[data-colors-shot]')?.dataset.color ||
          '',
        view: 0
      }));

      if (this.panes.length === 0) return;

      this._onSwatch = this.onSwatch.bind(this);
      this._onView = this.onView.bind(this);

      this.panes.forEach((pane) => {
        pane.swatches.forEach((swatch) => swatch.addEventListener('click', this._onSwatch));
        pane.views.forEach((view) => view.addEventListener('click', this._onView));
      });
    }

    disconnectedCallback() {
      this.panes?.forEach((pane) => {
        pane.swatches.forEach((swatch) => swatch.removeEventListener('click', this._onSwatch));
        pane.views.forEach((view) => view.removeEventListener('click', this._onView));
      });
    }

    paneOf(element) {
      return this.panes.find((pane) => pane.root.contains(element));
    }

    onSwatch(event) {
      const pane = this.paneOf(event.currentTarget);
      if (!pane) return;

      pane.color = event.currentTarget.dataset.color;
      pane.view = 0;
      this.render(pane);
    }

    onView(event) {
      const pane = this.paneOf(event.currentTarget);
      if (!pane) return;

      pane.view = Number(event.currentTarget.dataset.view) || 0;
      this.render(pane);
    }

    render(pane) {
      pane.shots.forEach((shot) => {
        shot.hidden = shot.dataset.color !== pane.color || Number(shot.dataset.view) !== pane.view;
      });

      pane.swatches.forEach((swatch) => {
        swatch.setAttribute('aria-pressed', String(swatch.dataset.color === pane.color));
      });

      pane.viewRows.forEach((row) => {
        row.hidden = row.dataset.color !== pane.color;
      });

      pane.views.forEach((view) => {
        const active = view.dataset.color === pane.color && Number(view.dataset.view) === pane.view;
        view.setAttribute('aria-pressed', String(active));
      });

      if (pane.current) pane.current.textContent = pane.color;
    }
  }

  /**
   * <offer-lp-gallery> — the hero photo carousel.
   *
   * The scrolling is entirely the browser's: the track is an overflow-x element
   * with scroll-snap, so swipe, trackpad, shift-wheel and arrow keys work with
   * no JS at all. This class adds only the three things the platform will not
   * give us: thumbnail navigation, an active-thumbnail state, and mouse drag.
   *
   * Active slide is derived from scroll POSITION — the slide nearest the track's
   * left edge — rather than from IntersectionObserver visibility. Position is
   * unambiguous and it agrees with where scroll-snap will settle; visibility
   * needs a threshold, and picking one that behaves at every slide width is
   * guesswork. It also stays correct if the slides are ever less than
   * full-width again.
   */
  class OfferLpGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));

      if (!this.track || this.slides.length === 0) return;

      this.arrows = this.querySelector('[data-gallery-arrows]');
      this.prev = this.querySelector('[data-gallery-prev]');
      this.next = this.querySelector('[data-gallery-next]');

      this._onThumb = this.onThumb.bind(this);
      this._onScroll = this.onScroll.bind(this);
      this._onPrev = () => this.step(-1);
      this._onNext = () => this.step(1);

      this.thumbs.forEach((thumb) => thumb.addEventListener('click', this._onThumb));
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this.prev?.addEventListener('click', this._onPrev);
      this.next?.addEventListener('click', this._onNext);

      // Only now that the handlers are attached do the arrows become real.
      if (this.arrows && this.slides.length > 1) this.arrows.hidden = false;

      this.activeIndex = null;
      this.setActive(0);
      this.enableDrag();
    }

    disconnectedCallback() {
      this.thumbs?.forEach((thumb) => thumb.removeEventListener('click', this._onThumb));
      this.track?.removeEventListener('scroll', this._onScroll);
      this.prev?.removeEventListener('click', this._onPrev);
      this.next?.removeEventListener('click', this._onNext);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.teardownDrag?.();
    }

    /**
     * Deliberately clamped rather than wrapping. With arrows disabled at the
     * ends, wrapping would contradict the disabled state the user can see.
     */
    step(direction) {
      const target = (this.activeIndex ?? 0) + direction;
      if (target < 0 || target > this.slides.length - 1) return;
      this.goTo(target);
    }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide) return;

      // scrollTo on the track, not scrollIntoView on the slide: the latter also
      // scrolls the PAGE to bring the hero into view, which yanks the viewport
      // when the control is used from further down.
      this.track.scrollTo({
        left: slide.offsetLeft - this.track.offsetLeft,
        behavior: 'smooth'
      });

      this.setActive(index);
    }

    /**
     * Scroll fires far more often than the answer changes, so the read is
     * coalesced into one animation frame.
     */
    onScroll() {
      if (this.frame) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.setActive(this.nearestIndex());
      });
    }

    nearestIndex() {
      const left = this.track.scrollLeft;
      const origin = this.track.offsetLeft;

      let bestIndex = 0;
      let bestDistance = Infinity;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - origin - left);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });

      return bestIndex;
    }

    setActive(index) {
      if (Number.isNaN(index) || index === this.activeIndex) return;
      this.activeIndex = index;

      this.thumbs.forEach((thumb) =>
        thumb.setAttribute('aria-pressed', String(Number(thumb.dataset.index) === index))
      );

      if (this.prev) this.prev.disabled = index === 0;
      if (this.next) this.next.disabled = index === this.slides.length - 1;

      this.syncVideo(index);
    }

    /**
     * A video slide plays only while it is the active one.
     *
     * The clips ship as data-src with preload="none" — two 1080p files is tens
     * of megabytes, and most visitors never leave the first slide — so the
     * source is attached on first activation and the download starts then.
     * Everything else pauses, including on the way past during a swipe, which
     * is what stops two clips playing over each other.
     */
    syncVideo(index) {
      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (!video) return;

        if (i !== index) {
          video.pause();
          return;
        }

        if (video.dataset.src) {
          video.src = video.dataset.src;
          delete video.dataset.src;
        }

        // Rejects under low-power mode and anywhere autoplay is declined. There
        // is nothing to recover from: the frame stays on screen either way, and
        // it is a product clip rather than something she came here to watch.
        video.play?.().catch(() => {});
      });
    }

    onThumb(event) {
      this.goTo(Number(event.currentTarget.dataset.index));
    }

    /**
     * Click-and-drag for mice. Touch already works natively and pointer events
     * would double-handle it, so `pointerdown` from a touch is ignored.
     */
    enableDrag() {
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let moved = 0;

      const onDown = (event) => {
        if (event.pointerType === 'touch') return;

        // A slide carrying native controls owns its own drags. Without this,
        // scrubbing the seek bar also scrolls the track, so the gallery moves to
        // the next slide while she is trying to find a moment in the clip.
        if (event.target.closest?.('video[controls]')) return;

        dragging = true;
        moved = 0;
        startX = event.clientX;
        startScroll = this.track.scrollLeft;
        this.track.classList.add('is-dragging');
      };

      const onMove = (event) => {
        if (!dragging) return;
        const delta = event.clientX - startX;
        moved = Math.abs(delta);
        this.track.scrollLeft = startScroll - delta;
      };

      const onUp = () => {
        if (!dragging) return;
        dragging = false;
        this.track.classList.remove('is-dragging');

        // Re-snap to the nearest slide. Removing .is-dragging restores
        // scroll-snap, but the browser does not re-run the snap on its own, so
        // the track would otherwise rest mid-slide.
        if (moved > 4) this.snapToNearest();
      };

      this.track.addEventListener('pointerdown', onDown);
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);

      this.teardownDrag = () => {
        this.track.removeEventListener('pointerdown', onDown);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
    }

    snapToNearest() {
      const slide = this.slides[this.nearestIndex()];
      if (!slide) return;

      this.track.scrollTo({
        left: slide.offsetLeft - this.track.offsetLeft,
        behavior: 'smooth'
      });
    }
  }

  /**
   * <offer-lp-fit> — the Size Fit Guarantee panel.
   *
   * Three jobs: filter the fit videos by height, drive the arrows on the rail
   * they sit in, and play one in a dialog.
   *
   * The filter is progressive enhancement in the strict sense. With the script
   * blocked every video is visible and playable through its own controls — the
   * chips simply do nothing. That is why the markup ships unfiltered rather
   * than pre-filtered to a default range.
   *
   * Video src is assigned on open and cleared on close. Leaving five 720p
   * sources in the DOM would have the browser fetching metadata for videos most
   * visitors never play, and leaving the src set after closing means a paused
   * file sits in memory for the rest of the session.
   */
  class OfferLpFit extends HTMLElement {
    connectedCallback() {
      this.chips = Array.from(this.querySelectorAll('[data-fit-filter]'));
      this.items = Array.from(this.querySelectorAll('[data-height]'));
      this.status = this.querySelector('[data-fit-status]');

      this.rail = this.querySelector('[data-fit-rail]');
      this.nav = this.querySelector('[data-fit-nav]');
      this.prev = this.querySelector('[data-fit-prev]');
      this.next = this.querySelector('[data-fit-next]');

      this.dialog = this.querySelector('[data-fit-dialog]');
      this.video = this.querySelector('[data-fit-video]');
      this.dialogCaption = this.querySelector('[data-fit-dialog-caption]');
      this.triggers = Array.from(this.querySelectorAll('[data-fit-play]'));

      this._onChip = this.onChip.bind(this);
      this._onPlay = this.onPlay.bind(this);
      this._onClose = this.close.bind(this);
      this._onDialogClose = this.onDialogClose.bind(this);
      this._onBackdrop = this.onBackdrop.bind(this);

      this.chips.forEach((chip) => chip.addEventListener('click', this._onChip));
      this.triggers.forEach((trigger) => trigger.addEventListener('click', this._onPlay));

      this.setupRail();

      this.querySelectorAll('[data-fit-close]').forEach((button) =>
        button.addEventListener('click', this._onClose)
      );

      if (this.dialog) {
        // `close` also fires for Esc, which is the whole reason for using
        // <dialog> — one teardown path regardless of how it was dismissed.
        this.dialog.addEventListener('close', this._onDialogClose);
        this.dialog.addEventListener('click', this._onBackdrop);
      }

      this.setupSizebay();
    }

    disconnectedCallback() {
      this.chips?.forEach((chip) => chip.removeEventListener('click', this._onChip));
      this.triggers?.forEach((trigger) => trigger.removeEventListener('click', this._onPlay));

      this.querySelectorAll('[data-fit-close]').forEach((button) =>
        button.removeEventListener('click', this._onClose)
      );

      if (this.dialog) {
        this.dialog.removeEventListener('close', this._onDialogClose);
        this.dialog.removeEventListener('click', this._onBackdrop);
      }

      this.openers?.forEach((button) => button.removeEventListener('click', this._onSizebay));
      clearInterval(this.sizebayPoll);
      this.releaseScrollHold();

      this.rail?.removeEventListener('scroll', this._onRailScroll);
      this.prev?.removeEventListener('click', this._onPrev);
      this.next?.removeEventListener('click', this._onNext);
      this.railResize?.disconnect();
      if (this.railFrame) cancelAnimationFrame(this.railFrame);
    }

    /* --------------------------------------------------------- Video rail */

    /**
     * The rail scrolls without any of this. What the platform will not give us
     * is a pair of arrows that know when they have run out of cards, so that is
     * all this adds — plus resetting the scroll position when the filter
     * changes, since a rail left scrolled to its end shows nothing at all once
     * it has been refilled with three cards.
     */
    setupRail() {
      if (!this.rail) return;

      this._onRailScroll = this.onRailScroll.bind(this);
      this._onPrev = () => this.stepRail(-1);
      this._onNext = () => this.stepRail(1);

      this.rail.addEventListener('scroll', this._onRailScroll, { passive: true });
      this.prev?.addEventListener('click', this._onPrev);
      this.next?.addEventListener('click', this._onNext);

      // Card width and how many fit both change with the viewport, and so does
      // whether the rail overflows at all. ResizeObserver rather than a window
      // listener because the panel is inside a container that can change width
      // without the window doing so — the theme editor resizes its preview.
      if (window.ResizeObserver) {
        this.railResize = new ResizeObserver(() => this.syncRail());
        this.railResize.observe(this.rail);
      }

      this.syncRail();
    }

    /**
     * One card short of a full screen, so the card that was cut off at the edge
     * is fully in view after a press and stays as the visual link between the
     * two positions. Paging by the exact viewport width would scroll that card
     * straight back out again, and there is no way to tell a rail that jumped a
     * whole screen from one that jumped two.
     */
    stepRail(direction) {
      if (!this.rail) return;

      const card = this.items.find((item) => !item.hidden);
      const stride = card ? card.getBoundingClientRect().width + this.railGap() : 200;
      const page = Math.max(stride, this.rail.clientWidth - stride);

      // Smooth is requested here rather than declared as scroll-behavior on the
      // rail, because the filter reset scrolls the same element and has to land
      // instantly — a declared smooth would animate that too, and a rail gliding
      // back from its end while its contents are being swapped underneath reads
      // as a glitch rather than as a reset.
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.rail.scrollBy({ left: direction * page, behavior: motion ? 'auto' : 'smooth' });
    }

    railGap() {
      return parseFloat(getComputedStyle(this.rail).columnGap) || 0;
    }

    /**
     * Reveals the arrows only when there is something off-screen to reach, and
     * disables each one at its end.
     *
     * The 2px tolerance is for subpixel widths: a rail scrolled fully right
     * routinely lands a fraction of a pixel short of scrollWidth, which would
     * otherwise leave the forward arrow enabled and doing nothing.
     */
    syncRail() {
      if (!this.rail) return;

      const overflow = this.rail.scrollWidth - this.rail.clientWidth;
      const scrollable = overflow > 2;

      if (this.nav) this.nav.hidden = !scrollable;
      if (!scrollable) return;

      const x = this.rail.scrollLeft;
      if (this.prev) this.prev.disabled = x <= 2;
      if (this.next) this.next.disabled = x >= overflow - 2;
    }

    onRailScroll() {
      // Coalesced to a frame: scroll fires far more often than the disabled
      // state can meaningfully change, and each pass reads layout.
      if (this.railFrame) return;

      this.railFrame = requestAnimationFrame(() => {
        this.railFrame = null;
        this.syncRail();
      });
    }

    /* ------------------------------------------------------ Sizebay fitter */

    /**
     * Wires this card's buttons to Sizebay's fit finder.
     *
     * Sizebay mounts its own buttons once per page — its module resolves the
     * anchor with querySelector — and on this page that mount is the buy box.
     * Rather than compete for it, these buttons post the messages Sizebay's own
     * photo overlay uses (`szb-open-fitting-room`, `szb-open-measurements`),
     * which it handles by clicking whichever real button matches. Same modal,
     * from anywhere on the page.
     */
    setupSizebay() {
      this.fitter = this.querySelector('[data-fit-sizebay]');
      if (!this.fitter) return;

      this.fallback = this.querySelector('[data-fit-sizebay-fallback]');
      this.openers = Array.from(this.querySelectorAll('[data-fit-sizebay-open]'));

      this._onSizebay = this.onSizebay.bind(this);
      this.openers.forEach((button) => button.addEventListener('click', this._onSizebay));

      this.whenSizebayReady();
    }

    /**
     * Sizebay loads its module, resolves the product against its own catalogue
     * and only then renders anything, so the buttons cannot be shown on trust.
     * Presence of #szb-vfr-button is the one honest signal that the modal these
     * buttons open actually exists.
     *
     * Give up after fifteen seconds. Sizebay fails outright when it cannot
     * resolve the product — which is every local dev server, since the
     * permalink it builds carries the dev host — and in that state the fallback
     * link is the correct thing to be left with rather than a control that
     * opens nothing.
     */
    whenSizebayReady() {
      const mounted = () => document.getElementById('szb-vfr-button');

      if (mounted()) {
        this.revealSizebay();
        return;
      }

      let waited = 0;

      this.sizebayPoll = setInterval(() => {
        waited += 250;

        if (mounted()) {
          clearInterval(this.sizebayPoll);
          this.revealSizebay();
        } else if (waited >= 15000) {
          clearInterval(this.sizebayPoll);
        }
      }, 250);
    }

    revealSizebay() {
      this.fitter.hidden = false;

      // The two would otherwise say the same thing twice, and the fallback is
      // the weaker of them — it only points at the page the finder is on.
      if (this.fallback) this.fallback.hidden = true;
    }

    onSizebay(event) {
      const message = event.currentTarget.dataset.fitSizebayOpen;
      if (!message) return;

      this.holdScrollThroughSizebay();
      window.postMessage(message, window.location.origin);
    }

    /**
     * Keeps the page where she left it when Sizebay's modal closes.
     *
     * Sizebay mounts its real buttons once per page, at the buy box, and the
     * buttons in this section work by asking it to click those. When the modal
     * closes it calls focus() on its own container — #szb-container, which sits
     * inside #build-your-bundle — and the browser scrolls that into view.
     * Measured on the live page: closing from this section moved the window from
     * 5648 to 661, about five thousand pixels up, for the measurements chart and
     * the fitting room alike. Read from here it looks like the page throwing her
     * back to the buy box for pressing Size guide.
     *
     * So the position is taken before the modal opens and put back once it has
     * gone. Keyed to Sizebay's own `vfr-modal-active` class on <html> rather
     * than to a close button, because the modal is an iframe on their domain
     * and can be dismissed from inside it, by the backdrop, or by finishing the
     * flow — the class going away is the one signal common to all of them.
     *
     * Esc does not close it. That is Sizebay's behaviour inside their iframe and
     * not something this can reach.
     */
    holdScrollThroughSizebay() {
      const root = document.documentElement;
      const ACTIVE_CLASS = 'vfr-modal-active';

      // Long enough for their iframe to load on a slow connection. If the modal
      // never opens there is nothing to correct, and the hold has to let go —
      // otherwise it fires against whatever she scrolled to minutes later.
      const OPEN_TIMEOUT = 6000;

      // The focus, and the scroll it causes, do not necessarily land in the same
      // frame as the class change, so the position is reasserted across a short
      // window rather than once.
      const SETTLE_MS = 400;

      this.releaseScrollHold();

      const target = window.scrollY;
      let opened = root.classList.contains(ACTIVE_CLASS);

      const settle = () => {
        // Any genuine input means she has taken the page over; a script that
        // keeps yanking it back at that point is worse than the jump.
        const surrender = () => this.releaseScrollHold();
        const events = ['wheel', 'touchstart', 'keydown'];
        events.forEach((type) =>
          window.addEventListener(type, surrender, { passive: true })
        );

        if (this.scrollHold) this.scrollHold.surrender = { surrender, events };

        const until = performance.now() + SETTLE_MS;

        const tick = () => {
          if (!this.scrollHold) return;

          if (Math.round(window.scrollY) !== Math.round(target)) {
            window.scrollTo(0, target);
          }

          if (performance.now() < until) {
            this.scrollHold.frame = requestAnimationFrame(tick);
          } else {
            this.releaseScrollHold();
          }
        };

        tick();
      };

      const observer = new MutationObserver(() => {
        if (root.classList.contains(ACTIVE_CLASS)) {
          opened = true;
          return;
        }

        if (opened) settle();
      });

      observer.observe(root, { attributes: true, attributeFilter: ['class'] });

      this.scrollHold = {
        observer,
        frame: 0,
        surrender: null,
        timeout: setTimeout(() => {
          if (!opened) this.releaseScrollHold();
        }, OPEN_TIMEOUT),
      };
    }

    releaseScrollHold() {
      if (!this.scrollHold) return;

      const { observer, frame, timeout, surrender } = this.scrollHold;

      // Cleared before the listeners so a queued frame sees no hold and stops.
      this.scrollHold = null;

      observer.disconnect();
      cancelAnimationFrame(frame);
      clearTimeout(timeout);

      if (surrender) {
        surrender.events.forEach((type) =>
          window.removeEventListener(type, surrender.surrender)
        );
      }
    }

    onChip(event) {
      const chip = event.currentTarget;
      const range = chip.dataset.fitFilter;

      this.chips.forEach((other) =>
        other.setAttribute('aria-pressed', String(other === chip))
      );

      let shown = 0;
      this.items.forEach((item) => {
        const match = range === 'all' || item.dataset.height === range;
        item.hidden = !match;
        if (match) shown += 1;
      });

      // Back to the first card. A rail left scrolled to its far end would
      // otherwise be sitting past the last of the three cards that replaced the
      // ten, so pressing a chip would appear to empty the panel.
      if (this.rail) {
        this.rail.scrollLeft = 0;
        this.syncRail();
      }

      if (this.status) {
        const label = chip.textContent.trim();
        this.status.textContent =
          shown === 1 ? `1 video, ${label}` : `${shown} videos, ${label}`;
      }
    }

    onPlay(event) {
      if (!this.dialog || !this.video) return;

      const trigger = event.currentTarget;
      const src = trigger.dataset.video;
      if (!src) return;

      // Remembered so focus can be handed back to the exact thumbnail that
      // opened the dialog, rather than to the top of the section.
      this.lastTrigger = trigger;

      const poster = trigger.dataset.poster;
      if (poster) this.video.setAttribute('poster', poster);

      this.video.src = src;

      if (this.dialogCaption) {
        this.dialogCaption.textContent = trigger.dataset.caption || '';
      }

      this.dialog.showModal();

      // Autoplay can be refused (low power mode, permissions). The controls are
      // already there, so a rejected promise is not an error worth surfacing.
      this.video.play?.().catch(() => {});
    }

    close() {
      this.dialog?.close();
    }

    /**
     * Clicks land on the dialog element itself only when they miss the inner
     * panel, which is how a backdrop click is detected without adding a
     * separate overlay element to trap it.
     */
    onBackdrop(event) {
      if (event.target === this.dialog) this.close();
    }

    onDialogClose() {
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.removeAttribute('poster');
        this.video.load();
      }

      this.lastTrigger?.focus();
      this.lastTrigger = null;
    }
  }

  /**
   * <offer-lp-photos> — full-size viewing for customer review photos.
   *
   * The thumbnails on the quote cards are cropped hard to 3:4 so every card sits
   * level with its neighbours, which means the crop discards part of each photo —
   * frequently the part showing the garment on the person. This gives that back.
   *
   * Same shape as the fit-video dialog: one native <dialog> reused for every
   * thumbnail, src assigned on open and cleared on close. Clearing matters more
   * here than it looks — these are unresized Loox originals, and leaving one
   * decoded in memory after closing costs several megabytes on a phone for an
   * image nobody is looking at.
   *
   * With the script blocked the thumbnails are inert buttons. That is why the
   * section only asks the quote snippet for buttons when it is also rendering the
   * dialog: the failure mode is a photo you cannot enlarge, not a control that
   * silently does nothing where a working one is implied.
   */
  class OfferLpPhotos extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-photo-dialog]');
      this.image = this.querySelector('[data-photo-img]');
      this.triggers = Array.from(this.querySelectorAll('[data-photo-open]'));

      if (!this.dialog || !this.image) return;

      this._onOpen = this.onOpen.bind(this);
      this._onClose = this.close.bind(this);
      this._onDialogClose = this.onDialogClose.bind(this);
      this._onBackdrop = this.onBackdrop.bind(this);

      this.triggers.forEach((trigger) => trigger.addEventListener('click', this._onOpen));

      this.querySelectorAll('[data-photo-close]').forEach((button) =>
        button.addEventListener('click', this._onClose)
      );

      // `close` also fires for Esc, so teardown has one path regardless of how
      // the dialog was dismissed.
      this.dialog.addEventListener('close', this._onDialogClose);
      this.dialog.addEventListener('click', this._onBackdrop);
    }

    disconnectedCallback() {
      this.triggers?.forEach((trigger) => trigger.removeEventListener('click', this._onOpen));

      this.querySelectorAll('[data-photo-close]').forEach((button) =>
        button.removeEventListener('click', this._onClose)
      );

      if (this.dialog) {
        this.dialog.removeEventListener('close', this._onDialogClose);
        this.dialog.removeEventListener('click', this._onBackdrop);
      }
    }

    onOpen(event) {
      const trigger = event.currentTarget;
      const src = trigger.dataset.full;
      if (!src) return;

      // Remembered so focus returns to the exact thumbnail that opened the
      // dialog rather than to the top of the section.
      this.lastTrigger = trigger;

      this.image.src = src;
      this.image.alt = trigger.dataset.caption || '';

      this.dialog.showModal();
    }

    close() {
      this.dialog?.close();
    }

    /**
     * Clicks land on the dialog element itself only when they miss the inner
     * panel, which is how a backdrop click is detected without adding a separate
     * overlay element to catch it.
     */
    onBackdrop(event) {
      if (event.target === this.dialog) this.close();
    }

    onDialogClose() {
      this.image.removeAttribute('src');
      this.image.alt = '';

      this.lastTrigger?.focus();
      this.lastTrigger = null;
    }
  }

  /* ---------------------------------------------------- Loox product tabs */

  /**
   * A Loox review wall that can switch between products, and can sit on a page
   * that already has another Loox wall.
   *
   * Loox's own placement does not stretch this far. Its script upgrades exactly
   * one #looxReviews container per page, and its resize protocol is the reason:
   * the frame posts its height along with a frame id, and the parent side does
   * `getElementById(frameId)`. With Loox's default that id is the hard-coded
   * `looxReviewsFrame`, so a second wall would either be ignored or steal the
   * first one's height messages.
   *
   * The way out is the `frame_id` parameter the widget accepts. Give each wall
   * its own id and the frame reports back under that name, so Loox's global
   * message listener — registered at script init whether or not it built any
   * widgets — resizes the right element. That is why this builds its own iframe
   * instead of borrowing Loox's, and why the container deliberately is not
   * called #looxReviews: nothing here should race Loox's own upgrade pass.
   *
   * The seeded `loox.reviews` metafield markup stays in the container as the
   * crawlable copy. It is display:none, so it costs nothing visually, and if
   * Loox never loads it is simply what remains.
   */
  class OfferLpReviews extends HTMLElement {
    static DEFAULT_FRAME_ID = 'looxReviewsFrame';

    connectedCallback() {
      this.wall = this.querySelector('[data-loox-wall]');
      if (!this.wall) return;

      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.productId = this.wall.dataset.productId;
      this.frameId = this.resolveFrameId();

      this.whenLooxReady(() => this.mount());

      if (this.tabs.length > 1) {
        this._onClick = this.onClick.bind(this);
        this._onKeydown = this.onKeydown.bind(this);
        this._onJumpIn = this.onJumpIn.bind(this);

        this.tabs.forEach((tab) => tab.addEventListener('click', this._onClick));
        this.addEventListener('keydown', this._onKeydown);

        // Delegated, because the links that jump here live in other sections
        // and there is no telling how many of them there are.
        document.addEventListener('click', this._onJumpIn);
      }
    }

    disconnectedCallback() {
      this.tabs?.forEach((tab) => tab.removeEventListener('click', this._onClick));
      this.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('click', this._onJumpIn);
      clearInterval(this.poll);
    }

    /**
     * A star rating elsewhere on the page has been clicked to come here.
     *
     * Each of those links carries the product it belongs to, so the vest's
     * rating opens the vest's reviews instead of dropping her on whichever tab
     * happens to be first — which is the jacket's, and reads as the wrong
     * number the moment she compares it with the rating she just clicked.
     *
     * The link's href does the scrolling on its own, so nothing is prevented
     * here; this only decides what she finds when she arrives.
     */
    onJumpIn(event) {
      const link = event.target.closest('[data-reviews-tab]');
      if (!link) return;

      const tab = this.tabs.find((candidate) => candidate.dataset.productId === link.dataset.reviewsTab);
      if (tab) this.select(tab);
    }

    /**
     * `looxReviewsFrame` is the id Loox gives the frame it builds itself, and
     * the one the widget reports when nothing tells it otherwise. Claiming it
     * means the height messages land whether or not the frame_id parameter is
     * honoured — worth the belt and braces, since frame_id is real in the
     * shipped code but absent from Loox's documentation.
     *
     * Only one element can hold that id. If Loox's own placement is on the page,
     * or another wall got there first, take a distinct id instead and depend on
     * frame_id on its own.
     */
    resolveFrameId() {
      const taken =
        document.getElementById(OfferLpReviews.DEFAULT_FRAME_ID) ||
        document.getElementById('looxReviews');

      return taken
        ? `tbReviewsFrame-${this.dataset.sectionId}`
        : OfferLpReviews.DEFAULT_FRAME_ID;
    }

    /**
     * Loox loads from the shop's global head snippet, so it is usually ready
     * before this runs — but not guaranteed, and on a blocked or ad-filtered
     * page it never will be. Give up quietly after ten seconds rather than
     * polling forever; the seeded markup is already in place.
     */
    whenLooxReady(callback) {
      const ready = () => window.LOOX?.root && window.LOOX?.clientId && window.LOOX?.hash;

      if (ready()) {
        callback();
        return;
      }

      let waited = 0;

      this.poll = setInterval(() => {
        waited += 100;

        if (ready()) {
          clearInterval(this.poll);
          callback();
        } else if (waited >= 10000) {
          clearInterval(this.poll);
        }
      }, 100);
    }

    mount() {
      const frame = document.createElement('iframe');

      frame.id = this.frameId;
      frame.title = 'Reviews widget';
      frame.width = '100%';
      frame.height = '0';
      frame.scrolling = 'no';

      // Loox injects its own widget the moment the container exists — there is
      // no IntersectionObserver anywhere in their bundle — so a wall this far
      // down the page would otherwise load in full on arrival. Owning the frame
      // is what lets us defer it.
      frame.loading = 'lazy';
      frame.setAttribute('frameborder', '0');
      frame.setAttribute('style', 'display:block;border:0;overflow:hidden;height:0;width:100%;');
      frame.src = this.widgetUrl(this.productId);

      this.wall.appendChild(frame);
      this.frame = frame;

      this.claimProductId();
    }

    /**
     * The frame's "write a review" message carries no product id — the parent
     * falls back to LOOX.productId, which on a page template comes from
     * ShopifyAnalytics and has nothing to do with which tab is open. Without
     * this, a review written from the vest tab is filed against the jacket.
     */
    claimProductId() {
      if (window.LOOX) window.LOOX.productId = Number(this.productId);
    }

    widgetUrl(productId) {
      const loox = window.LOOX;

      const params = new URLSearchParams({
        h: loox.hash,
        // Loox pages the wall itself past this, so it is a first-load size
        // rather than a cap. This read a data-limit attribute the section never
        // rendered, so it was always the fallback anyway.
        limit: '20',
        default_tab: 'automatic',
        frame_id: this.frameId,
      });

      if (loox.multilingual && loox.shopifyLocale) params.set('language', loox.shopifyLocale);

      return `${loox.root}/widget/${loox.clientId}/reviews/${productId}?${params}`;
    }

    onClick(event) {
      this.select(event.currentTarget);
    }

    /**
     * Roving tabindex, so the tab group is one stop in the tab order and the
     * arrow keys move between products inside it.
     */
    onKeydown(event) {
      const current = this.tabs.indexOf(document.activeElement);
      if (current === -1) return;

      let next = null;
      if (event.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.tabs.length - 1;

      if (next === null) return;

      event.preventDefault();
      this.tabs[next].focus();
      this.select(this.tabs[next]);
    }

    select(tab) {
      if (!tab || tab.getAttribute('aria-selected') === 'true') return;

      this.tabs.forEach((other) => {
        const isSelected = other === tab;
        other.setAttribute('aria-selected', String(isSelected));
        other.tabIndex = isSelected ? 0 : -1;
      });

      const panel = this.querySelector('[role="tabpanel"]');
      if (panel && tab.id) panel.setAttribute('aria-labelledby', tab.id);

      this.productId = tab.dataset.productId;
      this.wall.dataset.productId = this.productId;

      // Before Loox is ready there is no frame yet, and mount() will pick the
      // current productId up when it arrives.
      if (!this.frame) return;

      this.frame.src = this.widgetUrl(this.productId);
      this.claimProductId();
    }
  }

  /**
   * Kaching price check — a guardrail, not a feature.
   *
   * The price the page quotes comes from snippets/offer-lp-offers.liquid; the
   * price she pays comes from the Kaching deal, configured in the app. Liquid
   * can't read Kaching, so nothing stops the two drifting apart except this:
   * once the widget has rendered, if none of its bars shows the config price,
   * say so in the console. Visible on the local preview while building or
   * changing an offer, invisible to shoppers.
   */
  function checkKachingPrice() {
    const holder = document.querySelector('[data-olp-expected-price]');
    if (!holder) return;

    const expected = parseInt(holder.dataset.olpExpectedPrice, 10);
    if (!expected) return;

    // Kaching hydrates after the window load event, which on a heavy page can
    // be well past any fixed timeout — so wait for the bars to appear rather
    // than polling for a while and giving up.
    const check = () => {
      const prices = holder.querySelectorAll('.kaching-bundles__bar-price');
      if (!prices.length) return false;

      const shown = Array.from(prices, (el) =>
        Math.round(parseFloat(el.textContent.replace(/[^0-9.]/g, '')) * 100)
      );

      // Bars can exist a moment before their prices are filled in.
      if (shown.some(Number.isNaN)) return false;

      if (!shown.includes(expected)) {
        console.warn(
          `[offer-lp] Price mismatch: the page quotes ${expected / 100} (snippets/offer-lp-offers.liquid) ` +
            `but the Kaching widget's bars show ${shown.map((c) => c / 100).join(', ')}. ` +
            'Update one of them so they agree.'
        );
      }
      return true;
    };

    if (check()) return;

    const observer = new MutationObserver(() => {
      if (check()) observer.disconnect();
    });
    observer.observe(holder, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkKachingPrice);
  } else {
    checkKachingPrice();
  }

  if (!window.customElements.get('offer-lp-sticky')) {
    window.customElements.define('offer-lp-sticky', OfferLpSticky);
  }

  if (!window.customElements.get('offer-lp-showcase')) {
    window.customElements.define('offer-lp-showcase', OfferLpShowcase);
  }

  if (!window.customElements.get('offer-lp-clips')) {
    window.customElements.define('offer-lp-clips', OfferLpClips);
  }

  if (!window.customElements.get('offer-lp-colors')) {
    window.customElements.define('offer-lp-colors', OfferLpColors);
  }

  if (!window.customElements.get('offer-lp-fit')) {
    window.customElements.define('offer-lp-fit', OfferLpFit);
  }

  if (!window.customElements.get('offer-lp-gallery')) {
    window.customElements.define('offer-lp-gallery', OfferLpGallery);
  }

  if (!window.customElements.get('offer-lp-photos')) {
    window.customElements.define('offer-lp-photos', OfferLpPhotos);
  }

  if (!window.customElements.get('offer-lp-reviews')) {
    window.customElements.define('offer-lp-reviews', OfferLpReviews);
  }
})();
